"use client";

import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Marquee from "react-fast-marquee";
import {
  Globe,
  Menu,
  ChevronDown,
  Search,
} from "lucide-react";
import Link from "next/link";
import { type FormEvent, useEffect, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BrandLogo } from "@/components/shared/brand-logo";
import { cn } from "@/lib/utils";

const noticeItems = [
  "Verified suppliers are now easier to discover across products and categories.",
  "DoExim Mall and DoExhibitions are now live for buyers, exhibitors, and sourcing teams.",
  "Create your account to save suppliers, track inquiries, and manage sourcing faster.",
  "Need help getting started? Explore supplier profiles, products, and trade opportunities from one place.",
];

const categoryLinks = [
  "Industrial Machinery",
  "Electronics",
  "Textiles & Garments",
  "Chemicals",
  "Construction Materials",
  "Automobile Parts",
  "Agriculture Products",
  "Packaging Materials",
];

const buildCategoryHref = (category: string) =>
  `/welcome/categories?q=${encodeURIComponent(category)}`;

export function Header() {
  const router = useRouter();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [headerSearchQuery, setHeaderSearchQuery] = useState("");

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(() => {
        const currentScrollY = Math.max(window.scrollY, 0);
        const isScrollingDown = currentScrollY > lastScrollY;

        // Wide thresholds absorb the layout shift caused by collapsing
        // the utility rows, so the sticky header does not bounce open/closed.
        const hideAt = window.innerWidth < 640 ? 96 : 220;
        const showAt = window.innerWidth < 640 ? 24 : 64;

        if (isScrollingDown && currentScrollY > hideAt) {
          setIsScrolled(true);
        } else if (!isScrollingDown && currentScrollY < showAt) {
          setIsScrolled(false);
        }

        lastScrollY = currentScrollY;
        ticking = false;
      });
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleHeaderSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const query = headerSearchQuery.trim();
    const params = new URLSearchParams();

    if (query) {
      params.set("q", query);
    }

    router.push(
      params.toString()
        ? `/welcome/products?${params.toString()}`
        : "/welcome/products",
    );
  };

  return (
    <header className="sticky top-0 z-50 border-b bg-white dark:bg-slate-950 shadow-sm">
      {/* FIXED NOTICE STRIP */}
      <div className="bg-black text-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex h-9 items-center overflow-hidden text-[11px] sm:h-10 sm:text-sm">
            <Marquee
              speed={34}
              gradient={false}
              pauseOnHover
              className="min-w-0"
            >
              <div className="flex items-center">
                {noticeItems.map((item) => (
                  <span
                    key={item}
                    className="mr-7 inline-flex items-center text-white/90 sm:mr-10"
                  >
                    <span className="mr-2 text-amber-300 sm:mr-3">•</span>
                    <span className="font-medium leading-none">{item}</span>
                  </span>
                ))}
              </div>
            </Marquee>
          </div>
        </div>
      </div>

      {/* TOP UTILITY BAR - HIDE ON SCROLL */}
      <div
        className={cn(
          "overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out",
          isScrolled ? "max-h-0 opacity-0" : "max-h-10 opacity-100",
        )}
      >
        <div className="border-b">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="ml-auto flex h-10 items-center justify-end gap-4 overflow-x-auto whitespace-nowrap scrollbar-none text-xs sm:text-sm">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-muted-foreground hover:text-foreground">
                    <Globe className="h-3.5 w-3.5" />
                    <span>India</span>
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem>India</DropdownMenuItem>
                  <DropdownMenuItem>United States</DropdownMenuItem>
                  <DropdownMenuItem>China</DropdownMenuItem>
                  <DropdownMenuItem>United Kingdom</DropdownMenuItem>
                  <DropdownMenuItem>UAE</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Link
                href="/auth/register?type=supplier"
                className="text-muted-foreground hover:text-foreground"
              >
                Become a seller
              </Link>

              <div className="h-4 w-px bg-border/60 shrink-0" />

              <Link
                href="/welcome/faq"
                className="text-muted-foreground hover:text-foreground"
              >
                Help
              </Link>

              <Link
                href="/auth/login"
                className="text-muted-foreground hover:text-foreground"
              >
                Sign in
              </Link>

            </div>
          </div>
        </div>
      </div>

      {/* MAIN HEADER */}
      <div>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex h-14 sm:h-16 items-center justify-between gap-2 sm:gap-4">
            {/* LOGO */}
            <Link href="/" className="flex items-center gap-2 shrink-0">
              <BrandLogo
                width={140}
                height={20}
                className="h-9 sm:h-11 w-auto object-contain"
              />
            </Link>

            <div
              className={cn(
                "hidden flex-1 max-w-[520px] px-4 transition-all duration-300 lg:block",
                isScrolled
                  ? "opacity-100 scale-100"
                  : "opacity-0 scale-95 pointer-events-none",
              )}
            >
              <form onSubmit={handleHeaderSearchSubmit}>
                <div className="flex h-11 items-center gap-2 rounded-full border border-[#d7c35a] bg-[#f7e56a] px-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_4px_10px_rgba(120,85,0,0.12)]">
                  <Search className="h-4 w-4 shrink-0 text-[#7a650f]" />
                  <Input
                    value={headerSearchQuery}
                    onChange={(event) => setHeaderSearchQuery(event.target.value)}
                    placeholder="Search products, suppliers..."
                    className="h-auto border-0 bg-transparent px-0 text-sm font-medium text-[#7a650f] placeholder:text-[#7a650f]/70 focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </div>
              </form>
            </div>

            {/* NAVIGATION */}
            <nav className="hidden lg:flex items-center gap-6 flex-1 ml-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-sm font-medium hover:text-primary px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition">
                    Categories
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56">
                  {categoryLinks.map((category) => (
                    <DropdownMenuItem key={category} asChild>
                      <Link href={buildCategoryHref(category)}>{category}</Link>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem asChild>
                    <Link href="/welcome/categories" className="font-medium text-primary">
                      View all categories
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* <Link
                href="/welcome/products"
                className="text-sm font-medium hover:text-primary px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              >
                Products
              </Link> */}
              <Link
                href="/welcome/mall"
                className="text-sm font-medium hover:text-primary px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition whitespace-nowrap"
              >
                DoExim Mall
              </Link>
              {/* <Link
                href="/buying-requests"
                className="text-sm font-medium hover:text-primary px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              >
                Buying requests
              </Link> */}
              <Link
                href="/welcome/exhibitions"
                className="text-sm font-medium hover:text-primary px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              >
                DoExim Expo
              </Link>
            </nav>

            {/* ACTIONS */}
            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              <Link
                href="/auth/register?type=buyer"
                className="hidden h-11 items-center rounded-md border border-[#d7c35a] bg-[#f7e56a] px-5 text-sm font-semibold text-[#3b2f05] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_4px_10px_rgba(120,85,0,0.12)] transition hover:-translate-y-0.5 hover:bg-[#f3df58] sm:inline-flex"
              >
                <span>Create account</span>
              </Link>

              {/* MOBILE MENU */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <Menu className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* MOBILE NAV */}
          {mobileMenuOpen && (
            <nav className="lg:hidden border-t py-4 space-y-2">
              {[
                ["All categories", "/welcome/categories"],
                ["Products", "/welcome/products"],
                ["Suppliers", "/welcome/suppliers"],
                ["DoExim Mall", "/welcome/mall"],
                ["DoExhibitions", "/welcome/exhibitions"],
                ["Buying requests", "/buying-requests"],
                ["Trade shows", "/trade-shows"],
                ["Become a seller", "/auth/register?type=supplier"],
                ["Create account", "/auth/login"],
              ].map(([label, href]) => (
                <Link
                  key={label}
                  href={href}
                  className="block py-2 text-sm font-medium hover:text-primary"
                >
                  {label}
                </Link>
              ))}
            </nav>
          )}
        </div>
      </div>

      {/* QUICK LINKS - HIDE ON SCROLL */}
      {/* <div
        className={cn(
          "hidden overflow-hidden border-t transition-[max-height,opacity] duration-300 ease-in-out md:block",
          isScrolled ? "max-h-0 opacity-0" : "max-h-16 opacity-100"
        )}
      >
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex gap-6 py-2 text-xs overflow-x-auto">
            {[
              "RFQs",
              "Top Rankers", 
              "Most Profitables", 
              "New arrivals",
              "Verified suppliers",
              "Bulk deals",
              "Ready to ship",
              "Customizable products",
              "Sourcing Events & Expo",
              "DoLearn Export",
              "B2B Do Trade Blogs",
            ].map((item) => (
              <Link
                key={item}
                href="#"
                className="whitespace-nowrap text-muted-foreground hover:text-foreground"
              >
                {item}
              </Link>
            ))}
          </div>
        </div>
      </div> */}
    </header>
  );
}
